import { logAppEvent } from "@/api/logs";
import { ProjectHoursTable } from "@/components/project-hours-table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { auth, db } from "@/firebase";
import type { WorkerModel } from "@/firestore_types";
import {
  getProjectWorkerAssignments,
  serializeProjectWorkerAssignments,
} from "@/lib/projectAssignmentHelpers";
import { genRandomId } from "@/lib/randomId";
import { type TimeEntryData, validateTimeEntry } from "@/lib/timeEntryValidation";
import { dateTimeLocalESTToDate, timestampToDateTimeLocalEST } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/agency/time_tracking/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { workers: allWorkers } = useAllWorkers();
  const { projects } = useProjects();
  const { user } = useAuth();
  const myWorkers = allWorkers.filter((worker) => worker.agency?.id === auth.currentUser?.uid);

  // Get all projects where my workers are assigned
  const myProjects = useMemo(() => {
    const myWorkerIds = new Set(myWorkers.map((w) => w.id));
    return projects.filter((project) => {
      const assignedWorkers = getProjectWorkerAssignments(project);
      return assignedWorkers.some((assignment) => myWorkerIds.has(assignment.workerId));
    });
  }, [projects, myWorkers]);

  // Manual hours dialog states
  const [isManualHoursModalOpen, setIsManualHoursModalOpen] = useState(false);
  const [selectedWorkerForHours, setSelectedWorkerForHours] = useState<WorkerModel | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [addingManualHours, setAddingManualHours] = useState(false);
  const [manualHoursData, setManualHoursData] = useState<TimeEntryData>({
    clockInDateTime: "",
    clockOutDateTime: "",
    lunchStartDateTime: "",
    lunchEndDateTime: "",
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Filter workers based on selected project
  const availableWorkersForProject = useMemo(() => {
    if (!selectedProjectId) return myWorkers;

    const project = myProjects.find((p) => p.id === selectedProjectId);
    if (!project) return [];

    const assignedWorkerIds = new Set(getProjectWorkerAssignments(project).map((a) => a.workerId));

    return myWorkers.filter((worker) => assignedWorkerIds.has(worker.id));
  }, [selectedProjectId, myProjects, myWorkers]);

  // Manual hours functions
  const resetManualHoursForm = () => {
    setManualHoursData({
      clockInDateTime: "",
      clockOutDateTime: "",
      lunchStartDateTime: "",
      lunchEndDateTime: "",
      notes: "",
    });
    setValidationErrors([]);
    setSelectedProjectId("");
    setSelectedWorkerForHours(null);
  };

  const handleOpenManualHours = () => {
    const now = Timestamp.now();
    const nowString = timestampToDateTimeLocalEST(now);
    setManualHoursData({
      ...manualHoursData,
      clockInDateTime: nowString,
    });
    setIsManualHoursModalOpen(true);
  };

  const submitManualHours = async () => {
    // Validate the form
    const errors = validateTimeEntry(manualHoursData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!selectedWorkerForHours || !selectedProjectId || !manualHoursData.clockInDateTime) {
      toast.info("Please select a project and worker");
      return;
    }

    const project = myProjects.find((p) => p.id === selectedProjectId);
    if (!project) {
      toast.info("Invalid project selected");
      return;
    }

    setAddingManualHours(true);
    try {
      const currentUserId = user?.uid;
      if (!currentUserId) {
        toast.info("Invalid user session");
        return;
      }

      // Create date-time objects from datetime-local inputs (interpreting as EST)
      const clockInDateTime = dateTimeLocalESTToDate(manualHoursData.clockInDateTime);
      const clockOutDateTime = manualHoursData.clockOutDateTime
        ? dateTimeLocalESTToDate(manualHoursData.clockOutDateTime)
        : null;
      const lunchStartDateTime = manualHoursData.lunchStartDateTime
        ? dateTimeLocalESTToDate(manualHoursData.lunchStartDateTime)
        : undefined;
      const lunchEndDateTime = manualHoursData.lunchEndDateTime
        ? dateTimeLocalESTToDate(manualHoursData.lunchEndDateTime)
        : undefined;

      // Generate the clock ID
      const clockId = `${selectedProjectId}-${selectedWorkerForHours.id}-${genRandomId()}`;

      // Get assigned workers data for the project
      const projectWorkerAssignments = getProjectWorkerAssignments(project);
      const assignedWorkers = serializeProjectWorkerAssignments(projectWorkerAssignments);

      // Create ProjectHours entry - UNAPPROVED by default for agencies
      const projectHoursRef = doc(db, "ProjectHours", clockId);

      const projectHoursData = {
        customerId: project.customerId,
        assignedManagerIds: project.assignedManagerIds || [],
        projectId: selectedProjectId,
        workerId: selectedWorkerForHours.id,
        isDeleted: false,
        clockInLocation: {
          lat: 0, // Default location for manual entries
          lng: 0,
        },
        clockedInAt: Timestamp.fromDate(clockInDateTime),
        clockedIn: true,
        clockedOut: clockOutDateTime ? true : false,
        assignedWorkers: assignedWorkers,
        approved: false, // Always unapproved for agency entries
        manualEntry: true,
        manualEntryBy: currentUserId,
        manualEntryNotes: manualHoursData.notes || "Manual entry by agency",
        ...(clockOutDateTime && {
          clockedOutAt: Timestamp.fromDate(clockOutDateTime),
        }),
        ...(lunchStartDateTime &&
          lunchEndDateTime && {
            lunchStartTime: Timestamp.fromDate(lunchStartDateTime),
            lunchEndTime: Timestamp.fromDate(lunchEndDateTime),
            onLunch: false,
          }),
      };

      await setDoc(projectHoursRef, projectHoursData);

      // Log the manual entry event
      logAppEvent("manual_project_hours_added", {
        workerId: selectedWorkerForHours.id,
        projectId: selectedProjectId,
        workDate: clockInDateTime.toISOString().split("T")[0],
        managerId: currentUserId,
      });

      setIsManualHoursModalOpen(false);
      resetManualHoursForm();
      toast.info("Manual hours added successfully! Pending approval from manager.");
    } catch (error) {
      console.error("Error adding manual hours:", error);
      toast.info("Failed to add manual hours. Please try again.");
    } finally {
      setAddingManualHours(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-1 sm:p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Project Hours Overview</h1>
        <Button
          onClick={handleOpenManualHours}
          size="default"
          className="bg-primary hover:bg-primary/90 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Manual Hours
        </Button>
      </div>

      <ProjectHoursTable
        workerIds={myWorkers.map((worker) => worker.id)}
        allowEditAgencyNote={true}
        allowDeleteEntries={false}
        allowClickableProjects={false}
        allowApproval={false}
      />

      {/* Manual Hours Dialog */}
      <Dialog open={isManualHoursModalOpen} onOpenChange={setIsManualHoursModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Manual Hours</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-start">
                  <svg
                    className="mt-0.5 mr-2 h-5 w-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800">
                      Please fix the following errors:
                    </p>
                    <ul className="mt-1 list-inside list-disc text-sm text-red-600">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <div className="flex items-center">
                <svg
                  className="mr-2 h-5 w-5 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-orange-800">Agency Manual Entry</p>
                  <p className="text-sm text-orange-600">
                    This entry will be unapproved and will require manager approval.
                  </p>
                </div>
              </div>
            </div>

            {/* Project Selection */}
            <div>
              <Label
                htmlFor="project-select"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Project *
              </Label>
              <Select
                value={selectedProjectId}
                onValueChange={(value) => {
                  setSelectedProjectId(value);
                  setSelectedWorkerForHours(null); // Reset worker when project changes
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {myProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Worker Selection */}
            <div>
              <Label
                htmlFor="worker-select"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Worker *
              </Label>
              <Select
                value={selectedWorkerForHours?.id || ""}
                onValueChange={(value) => {
                  const worker = availableWorkersForProject.find((w) => w.id === value);
                  setSelectedWorkerForHours(worker || null);
                }}
                disabled={!selectedProjectId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={selectedProjectId ? "Select a worker" : "Select a project first"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableWorkersForProject.map((worker) => (
                    <SelectItem key={worker.id} value={worker.id}>
                      {worker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label
                  htmlFor="clock-in-datetime"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Clock In Date & Time *
                </Label>
                <Input
                  id="clock-in-datetime"
                  type="datetime-local"
                  value={manualHoursData.clockInDateTime}
                  onChange={(e) => {
                    setManualHoursData({
                      ...manualHoursData,
                      clockInDateTime: e.target.value,
                    });
                    setValidationErrors([]);
                  }}
                  className="w-full"
                  required
                />
              </div>
              <div>
                <Label
                  htmlFor="clock-out-datetime"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Clock Out Date & Time (Optional)
                </Label>
                <Input
                  id="clock-out-datetime"
                  type="datetime-local"
                  value={manualHoursData.clockOutDateTime}
                  onChange={(e) => {
                    setManualHoursData({
                      ...manualHoursData,
                      clockOutDateTime: e.target.value,
                    });
                    setValidationErrors([]);
                  }}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label
                  htmlFor="lunch-start-datetime"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Lunch Start Date & Time (Optional)
                </Label>
                <Input
                  id="lunch-start-datetime"
                  type="datetime-local"
                  value={manualHoursData.lunchStartDateTime}
                  onChange={(e) => {
                    setManualHoursData({
                      ...manualHoursData,
                      lunchStartDateTime: e.target.value,
                    });
                    setValidationErrors([]);
                  }}
                  className="w-full"
                />
              </div>
              <div>
                <Label
                  htmlFor="lunch-end-datetime"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Lunch End Date & Time (Optional)
                </Label>
                <Input
                  id="lunch-end-datetime"
                  type="datetime-local"
                  value={manualHoursData.lunchEndDateTime}
                  onChange={(e) => {
                    setManualHoursData({
                      ...manualHoursData,
                      lunchEndDateTime: e.target.value,
                    });
                    setValidationErrors([]);
                  }}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="mb-1 block text-sm font-medium text-gray-700">
                Notes (Optional)
              </Label>
              <textarea
                id="notes"
                value={manualHoursData.notes}
                onChange={(e) =>
                  setManualHoursData({
                    ...manualHoursData,
                    notes: e.target.value,
                  })
                }
                className="h-20 w-full resize-none rounded border px-3 py-2"
                placeholder="Add any additional notes about this manual entry..."
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setIsManualHoursModalOpen(false);
                resetManualHoursForm();
              }}
              className="rounded border px-4 py-2 text-gray-600 hover:bg-gray-50"
              disabled={addingManualHours}
            >
              Cancel
            </button>
            <button
              onClick={submitManualHours}
              disabled={addingManualHours}
              className="bg-primary hover:bg-primary/90 rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {addingManualHours ? "Adding..." : "Add Manual Hours"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
